import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import SearchIcon from '@mui/icons-material/Search';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../../api/client';
import type { CatalogueEntry, CatalogueRow, DiscoveryKind } from '../../api/types';
import { useApi } from '../../api/useApi';
import { useCatalogue } from '../../api/useCatalogue';
import { useDiscoveryState } from '../../api/useDiscoveryState';
import AsyncSection from '../AsyncSection';
import CatalogueDialog from './CatalogueDialog';
import Shelf from './Shelf';
import { useOpenCatalogueEntry } from './useOpenCatalogueEntry';

const rows: CatalogueRow[] = ['Trending', 'Popular', 'TopRated', 'New', 'Upcoming'];

/// Everything that exists, with what this server can do about each of it.
///
/// Most of what is here is not in the library - it is the world's catalogue, and the tiles that
/// are already here say so with a badge. That difference is the whole point: one of them is
/// something to watch now, the other is something to go and get.
///
/// It scrolls rather than paginates. The rows and the genres are the whole of the catalogue cut
/// five ways and twenty ways, and either way there is far more behind the first twenty tiles
/// than a person would ever click through.
export default function CatalogueBrowser({ kind = 'Any' }: { kind?: DiscoveryKind }) {
  const { t } = useTranslation();

  const [row, setRow] = useState<CatalogueRow>('Trending');
  const [typed, setTyped] = useState('');
  const [genreId, setGenreId] = useState<number>();
  const [opened, setOpened] = useState<CatalogueEntry>();
  const open = useOpenCatalogueEntry(setOpened);
  const { playsOnDemand } = useDiscoveryState();

  const query = useTypingSettled(typed);

  const genres = useApi(
    ['discovery', 'genres', kind],
    () => api.catalogueGenres(kind),
    { staleTime: 24 * 60 * 60 * 1000 },
  );

  const catalogue = useCatalogue({ row, kind, query: query || undefined, genreId });

  // A search is about one thing and the rows and genres are about everything; showing both at
  // once would say that a narrowed row is being searched, which it is not.
  const searching = query.length > 0;

  return (
    <Stack spacing={3}>
      <Stack
        direction="row"
        spacing={2}
        useFlexGap
        sx={{ alignItems: 'center', flexWrap: 'wrap' }}
      >
        <ToggleButtonGroup
          exclusive
          size="small"
          value={row}
          onChange={(_, next: CatalogueRow | null) => next && setRow(next)}
          disabled={searching}
        >
          {rows.map((candidate) => (
            <ToggleButton key={candidate} value={candidate}>
              {t(`discover.row.${candidate.toLowerCase()}`)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Box sx={{ flexGrow: 1 }} />

        <TextField
          size="small"
          value={typed}
          onChange={(event) => setTyped(event.target.value)}
          placeholder={t('discover.search')}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
      </Stack>

      {!searching && (genres.data ?? []).length > 0 && (
        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
          <Chip
            label={t('discover.genre.all')}
            size="small"
            color={genreId === undefined ? 'primary' : 'default'}
            variant={genreId === undefined ? 'filled' : 'outlined'}
            onClick={() => setGenreId(undefined)}
          />

          {(genres.data ?? []).map((genre) => (
            <Chip
              key={`${genre.kind}:${genre.id}`}
              label={genre.name}
              size="small"
              color={genreId === genre.id ? 'primary' : 'default'}
              variant={genreId === genre.id ? 'filled' : 'outlined'}
              onClick={() => setGenreId(genreId === genre.id ? undefined : genre.id)}
            />
          ))}
        </Stack>
      )}

      <AsyncSection loading={catalogue.loading} error={catalogue.error} onRetry={catalogue.reload}>
        {catalogue.entries.length === 0 ? (
          <Typography color="text.secondary">{t('discover.empty.detail')}</Typography>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
            }}
          >
            {catalogue.entries.map((entry, index) => (
              <CatalogueCard
                key={`${entry.title.provider}:${entry.title.kind}:${entry.title.id}:${index}`}
                entry={entry}
                showHeld={!playsOnDemand}
                onOpen={() => open(entry)}
              />
            ))}
          </Box>
        )}

        <MoreWhenReached
          hasMore={catalogue.hasMore}
          loading={catalogue.loadingMore}
          onReached={catalogue.loadMore}
        />
      </AsyncSection>

      <CatalogueDialog
        entry={opened}
        onClose={() => setOpened(undefined)}
        onFetched={() => void catalogue.reload()}
      />
    </Stack>
  );
}

/// Asks for the next page once the bottom of the list comes into view.
///
/// Watching the scroll position would mean doing arithmetic on every scroll event for something
/// the browser can answer itself; this asks it to say when the end is visible and does nothing
/// in between.
function MoreWhenReached({
  hasMore,
  loading,
  onReached,
}: {
  hasMore: boolean;
  loading: boolean;
  onReached: () => void;
}) {
  const sentinel = useRef<HTMLDivElement>(null);
  const reached = useRef(onReached);

  // Held in a ref rather than in the effect's dependencies: the callback is a new function on
  // every render, and watching it would tear the observer down and build it up again each time.
  useEffect(() => {
    reached.current = onReached;
  }, [onReached]);

  useEffect(() => {
    const element = sentinel.current;

    if (!element || !hasMore) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          reached.current();
        }
      },

      // A page ahead of the bottom edge, so the next tiles are there by the time the scroll
      // arrives rather than a moment after it.
      { rootMargin: '600px' },
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [hasMore]);

  if (!hasMore) {
    return null;
  }

  return (
    <Box ref={sentinel} sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
      {loading && <CircularProgress size={28} />}
    </Box>
  );
}

/// What somebody typed, once they stopped typing. Every keystroke is not a search.
function useTypingSettled(typed: string, after = 350) {
  const [settled, setSettled] = useState(typed);

  useEffect(() => {
    const timer = window.setTimeout(() => setSettled(typed.trim()), after);

    return () => window.clearTimeout(timer);
  }, [typed, after]);

  return settled;
}

/// One strip of the catalogue for a home screen: the first page of one row, sideways.
export function CatalogueRowStrip({
  title,
  row,
  kind = 'Any',
}: {
  title: string;
  row: CatalogueRow;
  kind?: DiscoveryKind;
}) {
  const [opened, setOpened] = useState<CatalogueEntry>();
  const open = useOpenCatalogueEntry(setOpened);
  const { playsOnDemand } = useDiscoveryState();
  const catalogue = useCatalogue({ row, kind });

  // An empty row is worse than no row, and a row that failed is not worth a warning on a home
  // screen - the browse pages say what went wrong.
  if (catalogue.entries.length === 0) {
    return null;
  }

  return (
    <>
      <Shelf title={title} columnWidth={170} gap={2}>
        {catalogue.entries.slice(0, 20).map((entry, index) => (
          <CatalogueCard
            key={`${entry.title.provider}:${entry.title.kind}:${entry.title.id}:${index}`}
            entry={entry}
            showHeld={!playsOnDemand}
            onOpen={() => open(entry)}
          />
        ))}
      </Shelf>

      <CatalogueDialog
        entry={opened}
        onClose={() => setOpened(undefined)}
        onFetched={() => void catalogue.reload()}
      />
    </>
  );
}

/// One tile. The badge is the whole message: here already, or to be fetched - unless everything
/// plays on demand, and then there is no difference to show.
export function CatalogueCard({
  entry,
  onOpen,
  showHeld = true,
}: {
  entry: CatalogueEntry;
  onOpen: () => void;
  showHeld?: boolean;
}) {
  const { t } = useTranslation();

  return (
    <Card variant="outlined">
      <CardActionArea onClick={onOpen} sx={{ display: 'block' }}>
        <Box sx={{ position: 'relative', aspectRatio: '2 / 3', bgcolor: 'action.hover' }}>
          {entry.title.posterUrl && (
            <Box
              component="img"
              src={entry.title.posterUrl}
              alt=""
              loading="lazy"
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          )}

          {/* In a mixed list a poster does not say whether it is a film or ten years of a
              programme, and the two are opened for different reasons. */}
          {entry.title.kind === 'Series' && (
            <Chip
              label={t('discover.badge.series')}
              size="small"
              sx={{
                position: 'absolute',
                top: 6,
                insetInlineStart: 6,
                height: 22,
                bgcolor: 'background.paper',
                fontWeight: 500,
              }}
            />
          )}

          {showHeld && entry.libraryItemId && (
            <PlayCircleIcon
              titleAccess={t('discover.badge.inLibrary')}
              sx={{
                position: 'absolute',
                top: 6,
                insetInlineEnd: 6,
                color: 'primary.main',
                bgcolor: 'background.paper',
                borderRadius: '50%',
              }}
            />
          )}
        </Box>

        <Stack spacing={0.5} sx={{ p: 1.25 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
            {entry.title.title}
          </Typography>

          {(entry.title.year !== undefined || entry.title.seasonCount !== undefined) && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {[
                entry.title.year,
                entry.title.seasonCount !== undefined
                  ? t('discover.seasons', { count: entry.title.seasonCount })
                  : undefined,
              ]
                .filter((part) => part !== undefined)
                .join(' · ')}
            </Typography>
          )}
        </Stack>
      </CardActionArea>
    </Card>
  );
}

/// Offered where the catalogue stands in for the library, so somebody can still reach the shelf.
export function CatalogueSwitch({
  showingCatalogue,
  onChange,
}: {
  showingCatalogue: boolean;
  onChange: (showingCatalogue: boolean) => void;
}) {
  const { t } = useTranslation();

  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={showingCatalogue}
      onChange={(_, next: boolean | null) => next !== null && onChange(next)}
    >
      <ToggleButton value>{t('library.mode.catalogue')}</ToggleButton>
      <ToggleButton value={false}>{t('library.mode.mine')}</ToggleButton>
    </ToggleButtonGroup>
  );
}
